import { ChangeEvent, useContext, useMemo, useState } from 'react'
import DiffViewer from 'react-diff-viewer-continued'

import { useArticleDetailApi, useArticleVersionDetailApi } from '@/common/api/modules/articles'
import { WidgetChoice } from '@/common/form/choice'
import { dateTimeStr } from '@/common/form/date'
import { ArticleRecord } from '@/common/model/media/article'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import LoadingObject, { LoadingObjectType } from '@/components/view/atoms/loading/LoadingObject'
import Selector, { SelectorSize } from '@/components/view/atoms/selector/Selector'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'

import { ArticleDiffModalContext } from './ArticleDiffModalProvider'

// コンテンツエリアは上部・下部の両方が編集・リライトの対象なので、どちらも差分比較する
const CONTENT_PART_TYPES = [
  { partType: 'content_area_top', label: 'コンテンツエリア上部' },
  { partType: 'content_area_bottom', label: 'コンテンツエリア下部' }
] as const

const versionChoiceLabel = (a: ArticleRecord): string =>
  `v${a.version_number} - ${dateTimeStr(a.created_at ?? '')}${a.site_operator_name ? ` ${a.site_operator_name}` : ''}`

interface ContentProps {
  siteId: number
  articleProposalId: number
  articleId?: number
}

// モーダルが開くたびに再マウントし、比較対象の初期選択を決定する
const ArticleDiffModalContent = ({ siteId, articleProposalId, articleId }: ContentProps) => {
  const { data: detail, isLoading: detailLoading } = useArticleDetailApi({
    site_id: siteId,
    article_proposal_id: articleProposalId
  })

  // history は version_number 降順（最新が先頭）
  const history = useMemo(() => detail?.history ?? [], [detail])

  const defaultNewId = articleId ?? history[0]?.id
  const defaultOldIndex = history.findIndex((a) => a.id === defaultNewId)
  const defaultOldId = defaultOldIndex >= 0 ? history[defaultOldIndex + 1]?.id : history[1]?.id

  const [newArticleId, setNewArticleId] = useState<number | undefined>(defaultNewId)
  const [oldArticleId, setOldArticleId] = useState<number | undefined>(defaultOldId)

  const choices: WidgetChoice<number>[] = useMemo(
    () => history.map((a) => ({ name: versionChoiceLabel(a), value: a.id })),
    [history]
  )

  const { data: oldVersion, isLoading: oldLoading } = useArticleVersionDetailApi(
    oldArticleId
      ? { site_id: siteId, article_proposal_id: articleProposalId, article_id: oldArticleId }
      : null
  )
  const { data: newVersion, isLoading: newLoading } = useArticleVersionDetailApi(
    newArticleId
      ? { site_id: siteId, article_proposal_id: articleProposalId, article_id: newArticleId }
      : null
  )

  // 両方が空のエリア（下部を使っていない記事など）は差分を出さない
  const diffAreas = CONTENT_PART_TYPES.map(({ partType, label }) => ({
    label,
    oldContent: oldVersion?.parts.find((p) => p.part_type === partType)?.content_html ?? '',
    newContent: newVersion?.parts.find((p) => p.part_type === partType)?.content_html ?? ''
  })).filter((a) => a.oldContent || a.newContent)

  if (detailLoading) {
    return (
      <ModalContent type={ModalContentType.NO_FRAME}>
        <Text size={14} color={TextColor.SECONDARY} align='center'>
          読み込み中です...
        </Text>
      </ModalContent>
    )
  }

  return (
    <ModalContent>
      <div className='flex flex-col gap-4'>
        <div className='flex flex-wrap items-center justify-between gap-4'>
          <div className='flex items-center gap-1'>
            <Badge badgeType={BadgeType.SQUARE_CRITICAL}>削除行</Badge>
            <Badge badgeType={BadgeType.SQUARE_SUCCESS}>追加行</Badge>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <Selector
              name='oldArticleId'
              size={SelectorSize.AUTO}
              choices={choices}
              value={oldArticleId}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setOldArticleId(e.target.value ? Number(e.target.value) : undefined)
              }
            />
            <Text size={14}>と</Text>
            <Selector
              name='newArticleId'
              size={SelectorSize.AUTO}
              choices={choices}
              value={newArticleId}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setNewArticleId(e.target.value ? Number(e.target.value) : undefined)
              }
            />
            <Text size={14}>の差分</Text>
          </div>
        </div>

        {!oldArticleId || !newArticleId ? (
          <Text size={14} color={TextColor.SECONDARY} align='center'>
            比較対象となるバージョンがありません
          </Text>
        ) : oldLoading || newLoading ? (
          <div className='flex flex-col gap-4 p-4'>
            <LoadingObject type={LoadingObjectType.TEXT} />
            <LoadingObject type={LoadingObjectType.TEXT} />
            <LoadingObject type={LoadingObjectType.TEXT} />
            <LoadingObject type={LoadingObjectType.TEXT} />
            <LoadingObject type={LoadingObjectType.TEXT} />
            <LoadingObject type={LoadingObjectType.TEXT} />
            <LoadingObject type={LoadingObjectType.TEXT} />
            <LoadingObject type={LoadingObjectType.TEXT} />
          </div>
        ) : (
          <div className='flex flex-col gap-4'>
            {diffAreas.map((area) => (
              <div key={area.label} className='flex flex-col gap-1'>
                <Text size={14} weight='bold'>
                  {area.label}
                </Text>
                <div className='border-primary overflow-hidden rounded-lg border'>
                  <DiffViewer
                    oldValue={area.oldContent}
                    newValue={area.newContent}
                    splitView
                    leftTitle={oldVersion ? versionChoiceLabel(oldVersion) : undefined}
                    rightTitle={newVersion ? versionChoiceLabel(newVersion) : undefined}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ModalContent>
  )
}

const ArticleDiffModal = () => {
  const { modal, showing, hide } = useContext(ArticleDiffModalContext)

  return (
    <Modal show={showing} type={ModalType.LARGE}>
      <ModalHeader text='差分の確認' onClickClose={hide} />
      {modal.showing && (
        <ArticleDiffModalContent
          siteId={modal.siteId}
          articleProposalId={modal.articleProposalId}
          articleId={modal.articleId}
        />
      )}
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={hide}>
          閉じる
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default ArticleDiffModal
